// const forge = require("node-forge");
import * as pkijs from "pkijs";
import * as asn1js from "asn1js";
import { Logger } from "@/logger/logger";
import forge from "node-forge";

import { checkRDNs } from "@/pki/subject";
import { ALLOWED_RDN_TYPES, ALLOWED_SAN_TYPES } from "@/pki/nameTypes";
import { toPEM } from "@/utils/cryptoutils";

const logger = new Logger("dfnpki");

/**
 * Redefinition of RelativeDistinguishedNames class from pkijs,
 * so the structure matches OpenSSL and what Bouncycastle expects.
 * Code is from https://github.com/PeculiarVentures/PKI.js/issues/169#issuecomment-384174083
 */
class AnotherRelativeDistinguishedNames extends pkijs.RelativeDistinguishedNames {
  constructor(parameters) {
    super(parameters);
  }

  toSchema() {
    //region Decode stored TBS value
    if (this.valueBeforeDecode.byteLength === 0) {
      // No stored encoded array, create "from scratch"
      return new asn1js.Sequence({
        value: Array.from(
          this.typesAndValues,
          (element) =>
            new asn1js.Set({
              value: [element.toSchema()],
            })
        ),
      });
    }

    const asn1 = asn1js.fromBER(this.valueBeforeDecode);
    //endregion

    //region Construct and return new ASN.1 schema for this object
    return asn1.result;
    //endregion
  }
}

/**
 * Erzeugt PKCS10-Requeste. Beispiel:
 * const builder = new RequestBuilder();
 * builder.addRDN('O', 'My Organization');
 * builder.addRDN('CN', 'My Name');
 * const promise = builder.build();
 * promise.then(({request, privateKey}) => doSomething(request, privateKey));
 */
export class RequestBuilder {
  constructor() {
    this.subject = [];
    this.altnames = [];
    this.keytype = "";
  }

  /**
   * Setzt das Subject dieses Antrags.
   *
   * @param {type} rdns ein Array mit {type, value}-Paaren, type muss einer der
   * ALLOWED_RDN_TYPES sein
   */
  setSubject(rdns) {
    checkRDNs(rdns);
    this.subject = [];
    for (let rdn in rdns) {
      if (Object.prototype.hasOwnProperty.call(rdns, rdn)) {
        for (let val of rdns[rdn]) {
          this.subject.push({
            type: ALLOWED_RDN_TYPES[rdn].oid,
            //shortName: rdn,
            value: val,
          });
        }
      }
    }
    // console.log("setSubject() this.subject: " + JSON.stringify(this.subject));
  }
  /**
   * Fügt einen RDN zum DN hinzu.
   *
   * @param {String} type ein erlaubter Typ
   * @param {String} value der zugehörige Wert
   */
  //  addRDN (type, value) {
  //        // logger.trace('Füge RDN hinzu: ' + type + ' = ' + value);
  //    if (this.allowedRDNTypes.indexOf(type) === -1) {
  //      throw new Error('RDNs vom Typ ' + type + ' können nicht hinzugefügt werden.')
  //    }
  //    this.subject.push({shortName: type, value: value})
  //  }

  /**
   * Setzt den Schlüsseltyp des Antrags.
   *
   * @param {String} keytype ein String aus der Dropdown Auswahl, der den
   * Schlüsseltyp und Größe bzw. Kurve bestimmt
   */
  setKeytype(keytype) {
    this.keytype = keytype;
    logger.debug("keytype: " + this.keytype);
  }

  /**
   * Gibt die richtigen Algorithmus Parameter zurück, abhängig vom Schlüsseltyp.
   *
   * @returns algorithm, ein Hash aus PKIjs, in dem die Algorithmus Parameter
   * enthalten sind
   */
  getKeyAlgo() {
    var algorithm = "";
    switch (this.keytype) {
      case "secp256r1":
        algorithm = pkijs.getAlgorithmParameters("ECDSA", "generateKey");
        algorithm.algorithm.namedCurve = "P-256";
        break;
      case "secp384r1":
        algorithm = pkijs.getAlgorithmParameters("ECDSA", "generateKey");
        algorithm.algorithm.namedCurve = "P-384";
        break;
      case "secp521r1":
        algorithm = pkijs.getAlgorithmParameters("ECDSA", "generateKey");
        algorithm.algorithm.namedCurve = "P-521";
        break;
      case "rsa2048":
        algorithm = pkijs.getAlgorithmParameters(
          "RSAES-PKCS1-V1_5",
          "generateKey"
        );
        algorithm.algorithm.modulusLength = 2048;
        break;
      case "rsa4096":
        algorithm = pkijs.getAlgorithmParameters(
          "RSAES-PKCS1-V1_5",
          "generateKey"
        );
        algorithm.algorithm.modulusLength = 4096;
        break;
    }
    return algorithm;
  }

  /**
   * Der Signaturalgorithmus muss auf den Schlüsseltyp und vor allem
   * die Kurve (bei EC) abgestimmt sein. Hier wird der richtige Parameter
   * zurückgegeben, der später von PKIjs benötigt wird.
   * @returns hash, der Hashalgorithmus der den Signaturalgorithmus entscheidet
   */
  getHashAlgo() {
    var hash = "";
    switch (this.keytype) {
      case "secp256r1":
        hash = "SHA-256";
        break;
      case "secp384r1":
        hash = "SHA-384";
        break;
      case "secp521r1":
        hash = "SHA-512";
        break;
      case "rsa2048":
        hash = "SHA-256";
        break;
      case "rsa4096":
        hash = "SHA-256";
        break;
    }
    return hash;
  }

  /**
   * Erzeugt einen PKCS10-Antrag aus den vorher gesetzten Daten. Das Erzeugen
   * findet asynchron statt, als Rückgabe gibt es eine Promise. Dem Aufruf von
   * resolve() wird dann ein Objekt {request: String, privateKey: Key} mit
   * PKCS10-Antrag und privatem Schlüssel als PEM übergeben.
   *
   * @returns {Promise} Promise, die resolve() mit Request und Key aufruft
   */
  async build() {
    const builder = this;

    const crypto = pkijs.getCrypto(true);

    const algorithm = this.getKeyAlgo();
    if (algorithm == "") {
      throw new Error(
        "Kein passender Algorithmus gefunden. Keytype ist: " + this.keytype
      );
    }

    // Präfix für PEM, wir haben nichts anderes als RSA oder EC
    var prefix = "";
    if (this.keytype.includes("rsa")) {
      prefix = "RSA ";
    } else {
      prefix = "EC ";
    }

    // KeyPair generieren
    const keys = await crypto.generateKey(
      algorithm.algorithm,
      true,
      algorithm.usages
    );

    // Public Key exportieren
    const spki = await crypto.exportKey("spki", keys.publicKey);
    const spkiPem = toPEM(spki, prefix + "PUBLIC KEY");
    logger.debug(`pem von spki: ${spkiPem}`);

    // Private Key exportieren
    const pkcs8 = await crypto.exportKey("pkcs8", keys.privateKey);
    const privateKey = toPEM(pkcs8, prefix + "PRIVATE KEY");

    logger.debug(
      `publickey: ${keys.publicKey.type}\n ${JSON.stringify(
        keys.publicKey.algorithm
      )}`
    );
    const pkcs10PEM = await builder.createCSR(keys);

    return { pkcs10PEM, privateKey };
  }

  /**
   * Erzeugt einen PKCS10-Antrag auf Basis der gesetzten Daten und dem
   * übergebenen Schlüsselpaar.
   *
   * @param {KeyPair} keys Schlüsselpaar
   * @returns {CSR} Antrag
   */
  async createCSR(keys) {
    const crypto = pkijs.getCrypto(true);

    const csr = new pkijs.CertificationRequest();

    // Subject setzen
    csr.subject = new AnotherRelativeDistinguishedNames();
    this.subject.forEach((rdn) =>
      csr.subject.typesAndValues.push(
        new pkijs.AttributeTypeAndValue({
          type: rdn.type,
          value: new asn1js.Utf8String({ value: rdn.value }), // TODO iwie muss der Typ vom value richtig für unterschiedliche typen gesetzt werden
        })
      )
    );

    logger.debug("subject importiert\n" + csr.subject.toSchema());
    csr.attributes = [];

    // Schlüssel importieren
    await csr.subjectPublicKeyInfo.importKey(keys.publicKey);

    if (this.getHashAlgo() == "") {
      throw new Error(
        "Kein passender Hash-Algorithmus gefunden. Keytype ist: " + this.keytype
      );
    }
    await csr.sign(keys.privateKey, this.getHashAlgo());

    const csrPEM = toPEM(
      csr.toSchema().toBER(false),
      "CERTIFICATE REQUEST"
    );
    logger.debug("csr pem\n" + csrPEM);
    return csrPEM;
  }

  /**
   * Setzt die Altnames.
   *
   * @param {Array} altnames Array mit type-value-Paaren
   */
  setAltnames(altnames) {
    const { email, Microsoft_UPN, URI } = ALLOWED_SAN_TYPES;
    this.altnames = [];

    for (let san of altnames) {
      switch (san.type) {
        case email:
        case URI:
          this.altnames.push({ type: san.type, value: san.value });
          break;
        case Microsoft_UPN:
          /** Dieser Fall ist noch nicht implementiert. Typ 0 steht für
           * 'otherName' und muss noch auf unbekannte Weise genauer
           * spezifiziert werden. Die zugehörige OID ist 1.3.6.1.4.1.311.20.2.3.
           * Vielleicht muss die verwendet werden.
           */
          break;
        default:
          throw new Error("Unbekannter SAN-Typ: " + JSON.stringify(san.type));
        // case IP: // Sonderfall: ip statt value verwenden!
        //  this.altnames.push({ type: san.type, ip: san.value });
      }
    }
  }
}
